import React, { useEffect, useState } from "react";
import { taskDetailApi } from "../../backend/client";
import {
  TaskDialog,
  TaskDialogActions,
  TaskMessage,
  TaskTextArea,
  TaskTextInput,
} from "./taskFormUi";

const emptyForm = {
  booking_company: "",
  contact_name: "",
  contact_phone: "",
  booking_date: "",
  booking_time: "",
  pickup_address: "",
  notes: "",
  reference_number: "",
};

export default function RubbishBookingForm({
  isOpen,
  onClose,
  projectId,
  taskId,
  onSave,
}: any) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState(emptyForm);

  const update = (key, value) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  useEffect(() => {
    if (!isOpen || !projectId) return;

    (async () => {
      setLoading(true);
      setErrorMessage("");
      const { data, error } = await taskDetailApi.list("rubbish-bookings", {
        project_id: Number(projectId),
        ...(taskId ? { task_id: taskId } : {}),
        order: "updated_at",
        ascending: false,
        limit: 1,
      });

      if (!error && data?.[0]) {
        const row = data[0];
        setForm({
          booking_company: row.booking_company ?? "",
          contact_name: row.contact_name ?? "",
          contact_phone: row.contact_phone ?? "",
          booking_date: row.booking_date ?? "",
          booking_time: row.booking_time ?? "",
          pickup_address: row.pickup_address ?? "",
          notes: row.notes ?? "",
          reference_number: row.reference_number ?? "",
        });
      } else {
        setForm(emptyForm);
      }
      setLoading(false);
    })();
  }, [isOpen, projectId, taskId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!projectId) return;

    setLoading(true);
    setErrorMessage("");

    const payload = {
      project_id: Number(projectId),
      task_id: taskId ?? null,
      booking_company: form.booking_company?.trim() || null,
      contact_name: form.contact_name?.trim() || null,
      contact_phone: form.contact_phone?.trim() || null,
      booking_date: form.booking_date || null,
      booking_time: form.booking_time || null,
      pickup_address: form.pickup_address?.trim() || null,
      notes: form.notes?.trim() || null,
      reference_number: form.reference_number?.trim() || null,
      updated_at: new Date().toISOString(),
    };

    const { error } = await taskDetailApi.upsert("rubbish-bookings", payload);

    setLoading(false);
    if (error) {
      setErrorMessage(error.message || "Failed to save rubbish booking");
      return;
    }

    onSave?.();
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <TaskDialog
      title="Book Rubbish Collection"
      description="Collection company, pickup timing, address, and booking reference."
      onClose={onClose}
      className="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <TaskMessage>{errorMessage}</TaskMessage>

        <div className="grid gap-4 sm:grid-cols-2">
          <TaskTextInput
            id="rubbish-booking-company"
            label="Booking Company"
            value={form.booking_company}
            onChange={(event) => update("booking_company", event.target.value)}
            placeholder="Company name"
            required
            className="sm:col-span-2"
          />
          <TaskTextInput
            id="rubbish-contact-name"
            label="Contact Name"
            value={form.contact_name}
            onChange={(event) => update("contact_name", event.target.value)}
            placeholder="Name"
          />
          <TaskTextInput
            id="rubbish-contact-phone"
            label="Contact Phone"
            type="tel"
            value={form.contact_phone}
            onChange={(event) => update("contact_phone", event.target.value)}
            placeholder="+61 ..."
          />
          <TaskTextInput
            id="rubbish-booking-date"
            label="Booking Date"
            type="date"
            value={form.booking_date}
            onChange={(event) => update("booking_date", event.target.value)}
            required
          />
          <TaskTextInput
            id="rubbish-booking-time"
            label="Booking Time"
            type="time"
            value={form.booking_time}
            onChange={(event) => update("booking_time", event.target.value)}
            required
          />
          <TaskTextInput
            id="rubbish-pickup-address"
            label="Pickup Address"
            value={form.pickup_address}
            onChange={(event) => update("pickup_address", event.target.value)}
            placeholder="Street, suburb, postcode"
            className="sm:col-span-2"
          />
          <TaskTextInput
            id="rubbish-reference"
            label="Reference / Job #"
            value={form.reference_number}
            onChange={(event) => update("reference_number", event.target.value)}
            placeholder="Optional"
          />
          <TaskTextArea
            id="rubbish-notes"
            label="Notes"
            value={form.notes}
            onChange={(event) => update("notes", event.target.value)}
            placeholder="Any instructions or special items..."
            rows={4}
            className="sm:col-span-2"
          />
        </div>

        <TaskDialogActions
          onCancel={onClose}
          submitType="submit"
          disabled={loading}
          submitLabel={loading ? "Saving..." : "Save Booking"}
        />
      </form>
    </TaskDialog>
  );
}
